import * as React from 'react'

import { Image } from '@thg-commerce/gravity-elements'
import { HorizontalAlignment } from '@thg-commerce/enterprise-core'
import { ProductBlockProps } from '@thg-commerce/enterprise-components'

import { ShopTheLookHotSpot } from './ShopTheLookHotSpot'
import {
  Description,
  ImageContainer,
  StyledEyeIcon,
  StyledHotSpot,
  Title,
  StyledCloseIcon,
  ShopTheLookContainer,
} from './styles'

export interface HotSpots {
  left: string
  top: string
  hotSpotProduct: ProductBlockProps & { rangeList?: string }
}

export interface ShopTheLookProps {
  title?: string
  horizontalAlignment?: HorizontalAlignment
  description?: string
  hotSpots: HotSpots[]
  image: {
    urls: {
      small: string
      medium: string
      large: string
    }
    alt?: string
  }
  className?: string
  iconPrimaryColor?: boolean
}

export const ShopTheLook = (props: ShopTheLookProps) => {
  const [openedEyeIndex, setOpenedEyeIndex] = React.useState(-1)
  const hotSpotRef = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    if (
      typeof document !== 'undefined' &&
      openedEyeIndex !== -1 &&
      hotSpotRef.current
    ) {
      hotSpotRef.current.focus()

      const headerHeight: number = document.getElementsByTagName('header')[0]
        ?.offsetHeight

      const containerHeight: number =
        document
          .getElementById('shop-the-look-image-container')
          ?.getBoundingClientRect().top || 0

      if (typeof window !== 'undefined' && headerHeight) {
        window.scrollTo({
          top: window.pageYOffset + containerHeight - headerHeight,
          behavior: 'smooth',
        })
      }
    }
  }, [openedEyeIndex, hotSpotRef])

  const toggleSelectedEye = (index: number) => {
    setOpenedEyeIndex((prevState) => (prevState !== index ? index : -1))
  }

  return (
    <ShopTheLookContainer
      hasItemActive={openedEyeIndex !== -1}
      className={props.className}
    >
      {props.title && (
        <Title horizontalAlignment={props.horizontalAlignment}>
          {props.title}
        </Title>
      )}
      {props.description && (
        <Description horizontalAlignment={props.horizontalAlignment}>
          {props.description}
        </Description>
      )}
      <ImageContainer id="shop-the-look-image-container">
        <Image
          src={[
            {
              url: props.image.urls.small,
              breakpoint: 'sm',
            },
            {
              url: props.image.urls.medium,
              breakpoint: 'md',
            },
            {
              url: props.image.urls.large,
              breakpoint: 'lg',
            },
          ]}
          width="100%"
          height="100%"
          alt={props.image.alt || props.title || ''}
          isAmp={false}
        />
        {props.hotSpots?.length &&
          props.hotSpots.map((hotSpot, index) => {
            const hotSpotKey = `hot-spot-eye-${index}`

            return openedEyeIndex === index ? (
              <div
                key={hotSpotKey}
                aria-label={hotSpot.hotSpotProduct?.title.value || hotSpotKey}
              >
                <StyledHotSpot
                  top={hotSpot.top}
                  left={hotSpot.left}
                  onClick={() => toggleSelectedEye(index)}
                  data-testid={`hot-spot-button-${index}`}
                  primary={props.iconPrimaryColor}
                >
                  <StyledCloseIcon
                    data-testid={`close-icon-${index}`}
                    primary={props.iconPrimaryColor}
                  />
                </StyledHotSpot>
                {hotSpot.hotSpotProduct && (
                  <ShopTheLookHotSpot
                    aria-labelledby={
                      hotSpot.hotSpotProduct?.title.value || hotSpotKey
                    }
                    hotSpotRef={hotSpotRef}
                    product={hotSpot.hotSpotProduct}
                    onCloseProductDetailsBox={() => toggleSelectedEye(index)}
                  />
                )}
              </div>
            ) : (
              <StyledHotSpot
                aria-label={hotSpot.hotSpotProduct?.title.value || hotSpotKey}
                key={hotSpotKey}
                top={hotSpot.top}
                left={hotSpot.left}
                onClick={() => toggleSelectedEye(index)}
                data-testid={`hot-spot-button-${index}`}
                primary={props.iconPrimaryColor}
              >
                <StyledEyeIcon
                  data-testid={`open-icon-${index}`}
                  primary={props.iconPrimaryColor}
                />
              </StyledHotSpot>
            )
          })}
      </ImageContainer>
    </ShopTheLookContainer>
  )
}
